import { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  FormCheck,
} from 'react-bootstrap';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '@context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { repositoryFactory } from '@data';

/**
 * Login Page Component
 * Proper login page with email and password validation
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRepository = repositoryFactory.getUserRepository();

  const resetTokenFromQuery = searchParams.get('resetToken') || searchParams.get('token') || '';

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // login | forgot | reset

  // UI state
  const [loading, setLoading] = useState(false);
  const [checkingBootstrap, setCheckingBootstrap] = useState(true);
  const [bootstrapRequired, setBootstrapRequired] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [setupError, setSetupError] = useState(null);
  const [setupSuccess, setSetupSuccess] = useState(null);
  const [setupData, setSetupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [setupValidationErrors, setSetupValidationErrors] = useState({});
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState(null);
  const [forgotSuccess, setForgotSuccess] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(null);

  useEffect(() => {
    const checkBootstrap = async () => {
      try {
        const users = await userRepository.getAllUsers();
        setBootstrapRequired(users.length === 0);
      } catch {
        setBootstrapRequired(false);
      } finally {
        setCheckingBootstrap(false);
      }
    };

    checkBootstrap();
  }, [userRepository]);

  useEffect(() => {
    if (resetTokenFromQuery && authMode !== 'reset') {
      setAuthMode('reset');
      setResetError(null);
      setForgotError(null);
      setError(null);
      return;
    }

    if (!resetTokenFromQuery && authMode === 'reset') {
      setAuthMode('login');
    }
  }, [resetTokenFromQuery, authMode]);

  const clearResetTokenFromUrl = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('resetToken');
    nextParams.delete('token');
    setSearchParams(nextParams, { replace: true });
  };

  const switchToLoginMode = () => {
    setAuthMode('login');
    setError(null);
    setForgotError(null);
    setResetError(null);
    if (resetTokenFromQuery) {
      clearResetTokenFromUrl();
    }
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  /**
    * Simple password hashing
   * In production, this should be done server-side
   */
  const hashPassword = (password) => {
    let hash = 0;
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  };

  /**
   * Validate form inputs
   */
  const validateForm = () => {
    const errors = {};

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSetupForm = () => {
    const errors = {};

    if (!setupData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!setupData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!setupData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(setupData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!setupData.password) {
      errors.password = 'Password is required';
    } else if (setupData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!setupData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (setupData.password !== setupData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setSetupValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateInitialAdmin = async (e) => {
    e.preventDefault();
    setSetupError(null);
    setSetupSuccess(null);

    if (!validateSetupForm()) {
      return;
    }

    setLoading(true);

    try {
      const normalizedFirstName = setupData.firstName.trim();
      const normalizedLastName = setupData.lastName.trim();
      const fullName = `${normalizedFirstName} ${normalizedLastName}`.trim();

      await userRepository.createInitialAdmin({
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        name: fullName,
        email: setupData.email,
        password: setupData.password,
      });

      setBootstrapRequired(false);
      setSetupSuccess('Initial admin account created. Please sign in.');
      setEmail(setupData.email.trim().toLowerCase());
      setPassword('');
      setSetupData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
      });
      setSetupValidationErrors({});
    } catch (err) {
      setSetupError(err.message || 'Failed to create initial admin account');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    setForgotError(null);
    setForgotSuccess(null);
    setResetSuccess(null);

    const normalizedEmail = String(forgotEmail || '').trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) {
      setForgotError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await userRepository.requestPasswordReset(normalizedEmail);
      setForgotSuccess(
        response?.message
        || 'If an account exists for this email, a password reset link has been sent.'
      );
    } catch (err) {
      setForgotError(err.message || 'Unable to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordWithToken = async (e) => {
    e.preventDefault();
    setResetError(null);
    setResetSuccess(null);

    if (!resetTokenFromQuery) {
      setResetError('This reset link is invalid or missing a token.');
      return;
    }

    if (!resetPassword) {
      setResetError('New password is required');
      return;
    }

    if (resetPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }

    if (resetPassword !== resetConfirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await userRepository.resetPasswordWithToken(resetTokenFromQuery, resetPassword);
      setResetSuccess('Password updated. You can now sign in with your new password.');
      setResetPassword('');
      setResetConfirmPassword('');
      clearResetTokenFromUrl();
      setAuthMode('login');
    } catch (err) {
      setResetError(err.message || 'Unable to reset password. Request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle login form submission
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const user = await userRepository.getUserByEmail(email);

      if (!user) {
        setError('No account found with this email address. Please check and try again.');
        setLoading(false);
        return;
      }

      // Check if account is active
      if (!user.isActive) {
        setError('Your account has been suspended. Please contact an administrator.');
        setLoading(false);
        return;
      }

      // Check if user has stored password hash
      const storedPasswordHash = user.passwordHash;
      const enteredPasswordHash = hashPassword(password);

      if (!storedPasswordHash) {
        // Account exists but was created via Google Sign-In — no password set
        const hint = import.meta.env.VITE_GOOGLE_CLIENT_ID
          ? ' This account uses Google Sign-In. Please use the "Sign in with Google" button below.'
          : '';
        setError(`No password is set for this account.${hint}`);
        setLoading(false);
        return;
      }

      if (storedPasswordHash !== enteredPasswordHash) {
        setError('Invalid email or password. Please try again.');
        setLoading(false);
        return;
      }

      // Best-effort login timestamp tracking for email/password sign-ins.
      const loginTimestamp = new Date().toISOString();
      let authenticatedUser = user;
      try {
        authenticatedUser = await userRepository.updateUser(user.id, {
          lastLoginAt: loginTimestamp,
        });
      } catch (trackingError) {
        authenticatedUser = {
          ...user,
          lastLoginAt: loginTimestamp,
        };
        console.warn('Unable to persist last login timestamp:', trackingError);
      }

      // Login and redirect
      login(authenticatedUser);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Google Sign-In credential response
   */
  const handleGoogleSuccess = async (credentialResponse) => {
    setError(null);
    setLoading(true);

    try {
      const user = await userRepository.loginWithGoogle(credentialResponse.credential);
      login(user);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Google Sign-In failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign-In was cancelled or failed. Please try again.');
  };

  if (checkingBootstrap) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="shadow-lg border-0">
              <Card.Body className="p-5 text-center">
                <Spinner animation="border" role="status" />
                <p className="mt-3 mb-0">Checking system setup...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card className="shadow-lg border-0">
            <Card.Header className="bg-primary text-white py-4">
              <h2 className="mb-0 text-center">Adaptive Learning</h2>
              <p className="text-center text-light-secondary mt-2 mb-0">
                {bootstrapRequired
                  ? 'Initial System Setup'
                  : authMode === 'forgot'
                    ? 'Request Password Reset'
                    : authMode === 'reset'
                      ? 'Choose a New Password'
                      : 'Sign In to Your Account'}
              </p>
            </Card.Header>
            <Card.Body className="p-5">
              {setupSuccess && (
                <Alert
                  variant="success"
                  dismissible
                  onClose={() => setSetupSuccess(null)}
                  className="mb-4"
                >
                  {setupSuccess}
                </Alert>
              )}

              {authMode === 'login' && error && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setError(null)}
                  className="mb-4"
                >
                  {error}
                </Alert>
              )}

              {authMode === 'forgot' && forgotError && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setForgotError(null)}
                  className="mb-4"
                >
                  {forgotError}
                </Alert>
              )}

              {authMode === 'forgot' && forgotSuccess && (
                <Alert
                  variant="success"
                  dismissible
                  onClose={() => setForgotSuccess(null)}
                  className="mb-4"
                >
                  {forgotSuccess}
                </Alert>
              )}

              {authMode === 'reset' && resetError && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setResetError(null)}
                  className="mb-4"
                >
                  {resetError}
                </Alert>
              )}

              {resetSuccess && (
                <Alert
                  variant="success"
                  dismissible
                  onClose={() => setResetSuccess(null)}
                  className="mb-4"
                >
                  {resetSuccess}
                </Alert>
              )}

              {bootstrapRequired ? (
                <>
                  <Alert variant="info" className="mb-4">
                    No users found. Create the first admin account to complete one-time setup.
                  </Alert>

                  {setupError && (
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => setSetupError(null)}
                      className="mb-4"
                    >
                      {setupError}
                    </Alert>
                  )}

                  <Form onSubmit={handleCreateInitialAdmin}>
                    <Row className="g-3 mb-1">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">First Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter first name"
                            value={setupData.firstName}
                            onChange={(e) => {
                              setSetupData({ ...setupData, firstName: e.target.value });
                              if (setupValidationErrors.firstName) {
                                setSetupValidationErrors({ ...setupValidationErrors, firstName: '' });
                              }
                            }}
                            isInvalid={!!setupValidationErrors.firstName}
                            disabled={loading}
                            size="lg"
                            className="border-2"
                          />
                          {setupValidationErrors.firstName && (
                            <Form.Control.Feedback type="invalid">
                              {setupValidationErrors.firstName}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">Last Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter last name"
                            value={setupData.lastName}
                            onChange={(e) => {
                              setSetupData({ ...setupData, lastName: e.target.value });
                              if (setupValidationErrors.lastName) {
                                setSetupValidationErrors({ ...setupValidationErrors, lastName: '' });
                              }
                            }}
                            isInvalid={!!setupValidationErrors.lastName}
                            disabled={loading}
                            size="lg"
                            className="border-2"
                          />
                          {setupValidationErrors.lastName && (
                            <Form.Control.Feedback type="invalid">
                              {setupValidationErrors.lastName}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Admin Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter admin email"
                        value={setupData.email}
                        onChange={(e) => {
                          setSetupData({ ...setupData, email: e.target.value });
                          if (setupValidationErrors.email) {
                            setSetupValidationErrors({ ...setupValidationErrors, email: '' });
                          }
                        }}
                        isInvalid={!!setupValidationErrors.email}
                        disabled={loading}
                        size="lg"
                        className="border-2"
                      />
                      {setupValidationErrors.email && (
                        <Form.Control.Feedback type="invalid">
                          {setupValidationErrors.email}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Create a strong password"
                        value={setupData.password}
                        onChange={(e) => {
                          setSetupData({ ...setupData, password: e.target.value });
                          if (setupValidationErrors.password) {
                            setSetupValidationErrors({ ...setupValidationErrors, password: '' });
                          }
                        }}
                        isInvalid={!!setupValidationErrors.password}
                        disabled={loading}
                        size="lg"
                        className="border-2"
                      />
                      {setupValidationErrors.password && (
                        <Form.Control.Feedback type="invalid">
                          {setupValidationErrors.password}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm password"
                        value={setupData.confirmPassword}
                        onChange={(e) => {
                          setSetupData({ ...setupData, confirmPassword: e.target.value });
                          if (setupValidationErrors.confirmPassword) {
                            setSetupValidationErrors({ ...setupValidationErrors, confirmPassword: '' });
                          }
                        }}
                        isInvalid={!!setupValidationErrors.confirmPassword}
                        disabled={loading}
                        size="lg"
                        className="border-2"
                      />
                      {setupValidationErrors.confirmPassword && (
                        <Form.Control.Feedback type="invalid">
                          {setupValidationErrors.confirmPassword}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      size="lg"
                      className="w-100 fw-semibold"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Creating Admin...
                        </>
                      ) : (
                        'Create Initial Admin'
                      )}
                    </Button>
                  </Form>
                </>
              ) : authMode === 'forgot' ? (
                <Form onSubmit={handleRequestPasswordReset}>
                  <Alert variant="info" className="mb-4">
                    Enter your account email and we will send a secure password reset link.
                  </Alert>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      isInvalid={Boolean(forgotEmail) && !isValidEmail(forgotEmail)}
                      disabled={loading}
                      size="lg"
                      className="border-2"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid email address
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      type="submit"
                      size="lg"
                      className="fw-semibold"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Sending Reset Link...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>

                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={switchToLoginMode}
                      disabled={loading}
                    >
                      Back to Sign In
                    </Button>
                  </div>
                </Form>
              ) : authMode === 'reset' ? (
                <Form onSubmit={handleResetPasswordWithToken}>
                  <Alert variant="info" className="mb-4">
                    Set a new password for your account. Reset links expire automatically for security.
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter a new password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      disabled={loading}
                      size="lg"
                      className="border-2"
                      autoComplete="new-password"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Confirm New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Confirm your new password"
                      value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      disabled={loading}
                      size="lg"
                      className="border-2"
                      autoComplete="new-password"
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      type="submit"
                      size="lg"
                      className="fw-semibold"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Resetting Password...
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </Button>

                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={switchToLoginMode}
                      disabled={loading}
                    >
                      Back to Sign In
                    </Button>
                  </div>
                </Form>
              ) : (
                <>
                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (validationErrors.email) {
                          setValidationErrors({
                            ...validationErrors,
                            email: '',
                          });
                        }
                      }}
                      isInvalid={!!validationErrors.email}
                      disabled={loading}
                      size="lg"
                      className="border-2"
                    />
                    {validationErrors.email && (
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.email}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (validationErrors.password) {
                          setValidationErrors({
                            ...validationErrors,
                            password: '',
                          });
                        }
                      }}
                      isInvalid={!!validationErrors.password}
                      disabled={loading}
                      size="lg"
                      className="border-2"
                    />
                    {validationErrors.password && (
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.password}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <FormCheck
                      type="checkbox"
                      id="rememberMe"
                      label="Remember my email"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                    />

                    <Button
                      variant="link"
                      type="button"
                      className="p-0 text-decoration-none"
                      onClick={() => {
                        setAuthMode('forgot');
                        setForgotEmail(String(email || '').trim());
                        setError(null);
                        setForgotError(null);
                        setForgotSuccess(null);
                      }}
                      disabled={loading}
                    >
                      Forgot password?
                    </Button>
                  </div>

                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    className="w-100 fw-semibold"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Form>

                  {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
                    <>
                      <div className="d-flex align-items-center my-3">
                        <hr className="flex-grow-1" />
                        <span className="px-3 text-muted small">or</span>
                        <hr className="flex-grow-1" />
                      </div>
                      <div className="d-flex justify-content-center">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          useOneTap={false}
                          disabled={loading}
                        />
                      </div>
                    </>
                  )}
                </>
              )}

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

